<script lang="ts">
import type { AvatarProps, ChipProps, ComponentConfig, KbdProps } from '@nuxt/ui';
import type { AppConfig } from 'nuxt/schema';
import type { SuggestionGroup, SuggestionItem } from './SuggestionMenu.vue';
import commandPalette from '#build/ui/command-palette';

type SuggestionMenuContent = ComponentConfig<typeof commandPalette, AppConfig, 'commandPalette'>;
export type SuggestionMenuContentUiSlots = Pick<SuggestionMenuContent['slots'], 'content' | 'viewport' | 'group' | 'label' | 'item' | 'itemLeadingIcon' | 'itemLeadingAvatarSize' | 'itemLeadingAvatar' | 'itemLeadingChipSize' | 'itemLeadingChip' | 'itemLabel' | 'itemLabelPrefix' | 'itemLabelBase' | 'itemLabelSuffix' | 'itemTrailing' | 'itemTrailingKbds' | 'itemTrailingKbdsSize' | 'itemTrailingHighlightedIcon' | 'itemTrailingIcon'>;

type SlotProps<T> = (props: { item: T; index: number }) => any;

export type SuggestionMenuContentSlots<G extends SuggestionGroup<T> = SuggestionGroup<any>, T extends SuggestionItem = SuggestionItem> = {
	'item': SlotProps<T>;
	'item-leading': SlotProps<T>;
	'item-label': SlotProps<T>;
	'item-trailing': SlotProps<T>;
} & Record<string, SlotProps<G>> & Record<string, SlotProps<T>>;
</script>

<script setup lang="ts" generic="G extends SuggestionGroup<T>, T extends SuggestionItem">
import { Primitive } from 'reka-ui';
import { tv } from 'tailwind-variants';
import { computed } from 'vue';
import { get } from '#layers/base/utils/object';
import { useMergedTheme } from '#layers/ui/composables/use-merged-theme';

const props = withDefaults(defineProps<{
	groups?: G[];
	items: T[];
	selectedIndex: number;

	labelKey?: string;

	ui?: SuggestionMenuContentUiSlots;
}>(), {
	labelKey: 'label',
});

const emit = defineEmits<{
	select: [item: T];
	activate: [item: T];
	close: [];
}>();

// eslint-disable-next-line vue/no-dupe-keys
const ui = computed(() => tv(useMergedTheme({ commandPalette }))());

const groupedItems = computed(() => {
	return props.items.reduce((acc, item) => {
		acc[item.group] ??= [];
		acc[item.group]!.push(item);
		return acc;
	}, {} as Record<string, T[]>);
});

const selectedItem = computed(() => props.items[props.selectedIndex]);

const filteredGroups = computed(() => props.groups?.filter((group) => groupedItems.value[group.id]?.length));

function handleSelect(item: T) {
	if (item.disabled) return;

	emit('select', item);
}

function handleHover(item: T) {
	if (item.disabled) return;

	emit('activate', item);
}
</script>

<template>
	<div :class="ui.content({ class: [props.ui?.content, 'min-w-40 border rounded-lg bg-default shadow-lg'] })" role="listbox">
		<div role="presentation" :class="ui.viewport({ class: props.ui?.viewport })">
			<div v-for="group in filteredGroups" :key="`group-${group.id}`" :class="ui.group({ class: props.ui?.group })" role="group">
				<p v-if="get(group, props.labelKey as string)" :class="ui.label({ class: props.ui?.label })">
					{{ get(group, props.labelKey as string) }}
				</p>

				<Primitive
					v-for="(item, index) in groupedItems[group.id]!"
					:key="`group-${group.id}-${index}`"
					tabindex="-1"
					as-child
				>
					<button
						:class="ui.item({ class: [props.ui?.item, item.ui?.item, item.class], active: item.active })"
						:data-highlighted="item.id === selectedItem?.id ? true : undefined"
						:data-disabled="item.disabled"
						:disabled="item.disabled"
						tabindex="-1"
						@mouseenter="handleHover(item)"
						@click="handleSelect(item)"
					>
						<slot :name="((item.slot || group.slot || 'item') as keyof SuggestionMenuContentSlots<G, T>)" :item="(item as any)" :index>
							<slot :name="((item.slot ? `${item.slot}-leading` : group.slot ? `${group.slot}-leading` : `item-leading`) as keyof SuggestionMenuContentSlots<G, T>)" :item="(item as any)" :index>
								<DIcon v-if="item.icon" :name="item.icon" :class="ui.itemLeadingIcon({ class: [props.ui?.itemLeadingIcon, item.ui?.itemLeadingIcon], active: item.active })" />
								<DAvatar v-else-if="item.avatar" :size="((item.ui?.itemLeadingAvatarSize || props.ui?.itemLeadingAvatarSize || ui.itemLeadingAvatarSize()) as AvatarProps['size'])" v-bind="item.avatar" :class="ui.itemLeadingAvatar({ class: [props.ui?.itemLeadingAvatar, item.ui?.itemLeadingAvatar], active: item.active })" />
								<DChip
									v-else-if="item.chip"
									:size="((item.ui?.itemLeadingChipSize || props.ui?.itemLeadingChipSize || ui.itemLeadingChipSize()) as ChipProps['size'])"
									inset
									standalone
									v-bind="item.chip"
									:class="ui.itemLeadingChip({ class: [props.ui?.itemLeadingChip, item.ui?.itemLeadingChip], active: item.active })"
								/>
							</slot>

							<span v-if="get(item, props.labelKey as string) || !!$slots[(item.slot ? `${item.slot}-label` : group.slot ? `${group.slot}-label` : `item-label`) as keyof SuggestionMenuContentSlots<G, T>]" :class="ui.itemLabel({ class: [props.ui?.itemLabel, item.ui?.itemLabel], active: item.active })">
								<slot :name="((item.slot ? `${item.slot}-label` : group.slot ? `${group.slot}-label` : `item-label`) as keyof SuggestionMenuContentSlots<G, T>)" :item="(item as any)" :index>
									<span v-if="item.prefix" :class="ui.itemLabelPrefix({ class: [props.ui?.itemLabelPrefix, item.ui?.itemLabelPrefix] })">{{ item.prefix }}</span>

									<span :class="ui.itemLabelBase({ class: [props.ui?.itemLabelBase, item.ui?.itemLabelBase], active: item.active })">{{ get(item, props.labelKey as string) }}</span>

									<span :class="ui.itemLabelSuffix({ class: [props.ui?.itemLabelSuffix, item.ui?.itemLabelSuffix], active: item.active })">{{ item.suffix }}</span>
								</slot>
							</span>

							<span :class="ui.itemTrailing({ class: [props.ui?.itemTrailing, item.ui?.itemTrailing] })">
								<slot :name="((item.slot ? `${item.slot}-trailing` : group.slot ? `${group.slot}-trailing` : `item-trailing`) as keyof SuggestionMenuContentSlots<G, T>)" :item="(item as any)" :index>
									<span v-if="item.kbds?.length" :class="ui.itemTrailingKbds({ class: [props.ui?.itemTrailingKbds, item.ui?.itemTrailingKbds] })">
										<DKbd v-for="(kbd, kbdIndex) in item.kbds" :key="kbdIndex" :size="((item.ui?.itemTrailingKbdsSize || props.ui?.itemTrailingKbdsSize || ui.itemTrailingKbdsSize()) as KbdProps['size'])" v-bind="typeof kbd === 'string' ? { value: kbd } : kbd" />
									</span>

									<DIcon v-else-if="group.highlightedIcon" :name="group.highlightedIcon" :class="ui.itemTrailingHighlightedIcon({ class: [props.ui?.itemTrailingHighlightedIcon, item.ui?.itemTrailingHighlightedIcon] })" />
								</slot>
							</span>
						</slot>
					</button>
				</Primitive>
			</div>
			<div
				v-if="!filteredGroups?.length"
				:class="ui.group({ class: props.ui?.group })"
			>
				<button
					:class="ui.item({ class: [props.ui?.item], active: true })"
					data-highlighted
					@click="emit('close')"
				>
					<span :class="[ui.itemLabel({ class: [props.ui?.itemLabel], active: true }), ui.itemLabelBase({ class: [props.ui?.itemLabelBase], active: true })]">
						{{ $t('common.no_results') }}
					</span>
				</button>
			</div>
		</div>
	</div>
</template>
